import { useMemo, useState } from "react";
import {
  AdminActionCancelled,
  clearTestData,
  cloneProductionData,
  generateTestBookings,
  loadDemoData,
  publishProduction,
} from "@/lib/adminDevActions";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { OccupancyTargetChart, type OccupancyPoint } from "@/pages/stammdaten/settings/OccupancyTargetChart";
import { devToolsVisibility } from "@/pages/stammdaten/settings/devToolsVisibility";
import { errorMessage } from "@/pages/stammdaten/settings/tenantSettingsTypes";

const MAX_RANGE_DAYS = 120;
const DEFAULT_OCCUPANCY = 60;
const DEFAULT_AVG_STAY = 4;

function Feedback({ success, error }: { success: string | null; error: string | null }) {
  return (
    <>
      {success ? <p className="text-sm text-status-green">{success}</p> : null}
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
    </>
  );
}

function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseIsoDate(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const date = new Date(`${iso}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function eachIsoDay(startIso: string, endIso: string): string[] {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (!start || !end || end < start) return [];
  const days: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push(toIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
    if (days.length > MAX_RANGE_DAYS) break;
  }
  return days;
}

function defaultRange(): { start: string; end: string } {
  const start = new Date();
  start.setHours(12, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 13);
  return { start: toIsoDate(start), end: toIsoDate(end) };
}

function rebuildPoints(
  start: string,
  end: string,
  previous: OccupancyPoint[],
  fallbackPercent = DEFAULT_OCCUPANCY,
): OccupancyPoint[] {
  const prev = new Map(previous.map((p) => [p.date, p.percent]));
  return eachIsoDay(start, end).map((date) => ({
    date,
    percent: prev.get(date) ?? fallbackPercent,
  }));
}

export function DevToolsSection({ canWrite }: { canWrite: boolean }) {
  const visibility = devToolsVisibility();
  const initialRange = useMemo(() => defaultRange(), []);

  const [demoSeedToday, setDemoSeedToday] = useState("");
  const [demoSeedReset, setDemoSeedReset] = useState(false);
  const [demoSeedLoading, setDemoSeedLoading] = useState(false);
  const [demoSeedSuccess, setDemoSeedSuccess] = useState<string | null>(null);
  const [demoSeedError, setDemoSeedError] = useState<string | null>(null);
  const [clearTestDataLoading, setClearTestDataLoading] = useState(false);
  const [clearTestDataSuccess, setClearTestDataSuccess] = useState<string | null>(null);
  const [clearTestDataError, setClearTestDataError] = useState<string | null>(null);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneSuccess, setCloneSuccess] = useState<string | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);

  const [testStart, setTestStart] = useState(initialRange.start);
  const [testEnd, setTestEnd] = useState(initialRange.end);
  const [avgStayNights, setAvgStayNights] = useState(String(DEFAULT_AVG_STAY));
  const [flatPercent, setFlatPercent] = useState(String(DEFAULT_OCCUPANCY));
  const [occupancyPoints, setOccupancyPoints] = useState<OccupancyPoint[]>(() =>
    rebuildPoints(initialRange.start, initialRange.end, []),
  );
  const [testGenLoading, setTestGenLoading] = useState(false);
  const [testGenSuccess, setTestGenSuccess] = useState<string | null>(null);
  const [testGenError, setTestGenError] = useState<string | null>(null);

  const rangeDayCount = occupancyPoints.length;
  const rangeTooLong = rangeDayCount > MAX_RANGE_DAYS;
  const rangeInvalid =
    !parseIsoDate(testStart) ||
    !parseIsoDate(testEnd) ||
    (parseIsoDate(testEnd) as Date) < (parseIsoDate(testStart) as Date);

  function applyDateRange(nextStart: string, nextEnd: string) {
    setTestStart(nextStart);
    setTestEnd(nextEnd);
    setOccupancyPoints((prev) => rebuildPoints(nextStart, nextEnd, prev));
  }

  function setAllOccupancy(percent: number) {
    const value = Math.max(0, Math.min(100, Math.round(percent)));
    setOccupancyPoints((prev) => prev.map((point) => ({ ...point, percent: value })));
  }

  function applyLinearRamp() {
    setOccupancyPoints((prev) => {
      if (prev.length <= 1) return prev.map((p) => ({ ...p, percent: 50 }));
      return prev.map((point, index) => ({
        ...point,
        percent: Math.round((index / (prev.length - 1)) * 100),
      }));
    });
  }

  function applyRandomCurve() {
    setOccupancyPoints((prev) => {
      if (prev.length === 0) return prev;
      let current = 40 + Math.random() * 30;
      return prev.map((point) => {
        current += (Math.random() - 0.5) * 24;
        current = Math.max(10, Math.min(95, current));
        return { ...point, percent: Math.round(current) };
      });
    });
  }

  async function onPublishProduction() {
    if (!canWrite) return;
    setPublishLoading(true);
    setPublishSuccess(null);
    setPublishError(null);
    try {
      setPublishSuccess(await publishProduction());
    } catch (err) {
      if (!(err instanceof AdminActionCancelled)) {
        setPublishError(errorMessage(err, "Veröffentlichung konnte nicht gestartet werden"));
      }
    } finally {
      setPublishLoading(false);
    }
  }

  async function onCloneProductionData() {
    if (!canWrite) return;
    setCloneLoading(true);
    setCloneSuccess(null);
    setCloneError(null);
    try {
      setCloneSuccess(await cloneProductionData());
    } catch (err) {
      if (!(err instanceof AdminActionCancelled)) {
        setCloneError(errorMessage(err, "Produktionsdaten konnten nicht geladen werden"));
      }
    } finally {
      setCloneLoading(false);
    }
  }

  async function onClearTestData() {
    if (!canWrite) return;
    setClearTestDataLoading(true);
    setClearTestDataSuccess(null);
    setClearTestDataError(null);
    try {
      setClearTestDataSuccess(await clearTestData());
    } catch (err) {
      if (!(err instanceof AdminActionCancelled)) {
        setClearTestDataError(errorMessage(err, "Testdaten konnten nicht gelöscht werden"));
      }
    } finally {
      setClearTestDataLoading(false);
    }
  }

  async function onLoadDemoData() {
    if (!canWrite) return;
    setDemoSeedLoading(true);
    setDemoSeedSuccess(null);
    setDemoSeedError(null);
    try {
      setDemoSeedSuccess(
        await loadDemoData({
          reset: demoSeedReset,
          today: demoSeedToday.trim() || undefined,
          confirm: false,
        }),
      );
    } catch (err) {
      if (!(err instanceof AdminActionCancelled)) {
        setDemoSeedError(errorMessage(err, "Demodaten konnten nicht geladen werden"));
      }
    } finally {
      setDemoSeedLoading(false);
    }
  }

  async function onGenerateTestBookings() {
    if (!canWrite || rangeInvalid || rangeTooLong) return;
    const avg = Number(avgStayNights);
    if (!Number.isFinite(avg) || avg < 1 || avg > 21) {
      setTestGenError("Durchschnittliche Übernachtungsdauer muss zwischen 1 und 21 liegen.");
      return;
    }
    setTestGenLoading(true);
    setTestGenSuccess(null);
    setTestGenError(null);
    try {
      setTestGenSuccess(
        await generateTestBookings({
          startDate: testStart,
          endDate: testEnd,
          avgStayNights: avg,
          dailyOccupancy: occupancyPoints,
          confirm: false,
        }),
      );
    } catch (err) {
      if (!(err instanceof AdminActionCancelled)) {
        setTestGenError(errorMessage(err, "Testdaten konnten nicht erzeugt werden"));
      }
    } finally {
      setTestGenLoading(false);
    }
  }

  if (!visibility.any) {
    return (
      <Card title="Testsystem" bodyClassName="text-sm text-label-secondary">
        <p>Auf diesem System sind keine Entwicklungs- oder Schulungswerkzeuge verfügbar.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {visibility.showPublishProduction ? (
        <Card title="Production veröffentlichen" bodyClassName="space-y-4">
          <p className="text-sm text-label-secondary">
            Überträgt den aktuellen Stand von dev.app auf app.: campdesk-api und campdesk-frontend
            werden von branch dev nach main promotet. Anschließend deployt die GitHub Action die API
            nach /opt/campdesk-api und wendet Datenbank-Migrationen auf campdesk_db an. Das Frontend
            wird über Netlify aus main neu gebaut.
          </p>
          <Feedback success={publishSuccess} error={publishError} />
          {canWrite ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="primary"
                loading={publishLoading}
                onClick={() => void onPublishProduction()}
              >
                Auf Production veröffentlichen
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {visibility.showProductionClone ? (
        <Card title="Produktionsdaten laden (Test)" bodyClassName="space-y-4">
          <p className="text-sm text-label-secondary">
            Kopiert den aktuellen Datenstand aus der Produktionsumgebung (Mandant default) in den
            Schulungs-Mandanten test: Stammdaten, Einstellungen, Gäste, Buchungen, Rechnungen und
            alle weiteren Fachdaten. So lassen sich Produktionsprobleme auf test.app.* nachstellen,
            ohne die Produktionsdaten zu verändern. Vorhandene Testdaten werden dabei ersetzt.
          </p>
          <Feedback success={cloneSuccess} error={cloneError} />
          {canWrite ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="primary"
                loading={cloneLoading}
                onClick={() => void onCloneProductionData()}
              >
                Produktionsdaten laden
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {visibility.showClearTestData ? (
        <Card title="Testdaten löschen (Dev)" bodyClassName="space-y-4">
          <p className="text-sm text-label-secondary">
            Entfernt alle Fachdaten des aktuellen Mandanten restlos: Gäste, Buchungen, Rechnungen,
            Zahlungen, Verträge, Tourist-Importe (inkl. hochgeladener Dateien) und Audit-Protokolle.
            Stammdaten wie Stellplätze, Platzkategorien, Artikel, Konten, Rabatte und Einstellungen
            bleiben unverändert. Die Konfiguration von Gäste-Tablets (Arbeitsplätze, Geräte,
            Inhalte) und Infobildschirmen (Bildschirme, Geräte, Seiten, Veranstaltungen) wird nicht
            gelöscht.
          </p>
          <Feedback success={clearTestDataSuccess} error={clearTestDataError} />
          {canWrite ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="danger"
                loading={clearTestDataLoading}
                onClick={() => void onClearTestData()}
              >
                Alle Testdaten löschen
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {visibility.showDemoSeedTools ? (
        <Card title="Testdaten für Zeitraum" bodyClassName="space-y-4">
          <p className="text-sm text-label-secondary">
            Erzeugt zufällige Buchungen für den gewählten Zeitraum. Pro Tag steuert der Graph die
            Ziel-Auslastung der Touristenplätze (0–100&nbsp;%). Bestehende Belegung bleibt erhalten;
            an Tagen, die schon über dem Ziel liegen, werden keine Buchungen ergänzt. Die
            Aufenthaltsdauer streut stark um die durchschnittliche Übernachtungsdauer.
          </p>
          <fieldset className="space-y-4" disabled={!canWrite}>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="flex flex-col gap-1 text-sm">
                Von
                <input
                  type="date"
                  className="eh-input"
                  value={testStart}
                  onChange={(e) => applyDateRange(e.target.value, testEnd)}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Bis
                <input
                  type="date"
                  className="eh-input"
                  value={testEnd}
                  onChange={(e) => applyDateRange(testStart, e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Ø Übernachtungen
                <input
                  type="number"
                  min={1}
                  max={21}
                  step={0.5}
                  className="eh-input"
                  value={avgStayNights}
                  onChange={(e) => setAvgStayNights(e.target.value)}
                />
              </label>
            </div>

            {rangeInvalid ? (
              <p className="text-sm text-status-red">Bitte einen gültigen Zeitraum wählen.</p>
            ) : null}
            {rangeTooLong ? (
              <p className="text-sm text-status-red">
                Zeitraum maximal {MAX_RANGE_DAYS} Tage (aktuell {rangeDayCount}).
              </p>
            ) : (
              <p className="text-xs text-label-tertiary">{rangeDayCount} Tage im Zeitraum</p>
            )}

            <div className="flex flex-wrap items-end gap-2">
              <label className="flex w-28 flex-col gap-1 text-sm">
                Alle auf %
                <input
                  type="number"
                  min={0}
                  max={100}
                  className="eh-input"
                  value={flatPercent}
                  onChange={(e) => setFlatPercent(e.target.value)}
                />
              </label>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setAllOccupancy(Number(flatPercent) || 0)}
              >
                Übernehmen
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={applyLinearRamp}>
                Rampe 0→100
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={applyRandomCurve}>
                Zufalls-Kurve
              </Button>
            </div>

            <OccupancyTargetChart
              points={occupancyPoints}
              onChange={setOccupancyPoints}
              disabled={!canWrite}
            />
          </fieldset>
          <Feedback success={testGenSuccess} error={testGenError} />
          {canWrite ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="primary"
                loading={testGenLoading}
                disabled={rangeInvalid || rangeTooLong}
                onClick={() => void onGenerateTestBookings()}
              >
                Testdaten generieren
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {visibility.showDemoSeedTools ? (
        <Card title="Demodaten (Dev)" bodyClassName="space-y-4">
          <p className="text-sm text-label-secondary">
            Lädt einen Beispiel-Campingbetrieb ab 01.04.2026 in die Datenbank des aktuellen
            Mandanten (Gäste, Buchungen, Rechnungen, Zahlungen). Verfügbar auf Dev- und
            Schulungs-Systemen (test.app.*).
          </p>
          <fieldset className="space-y-3" disabled={!canWrite}>
            <label className="flex max-w-xs flex-col gap-1 text-sm">
              Simulations-Stichtag (optional)
              <input
                type="date"
                className="eh-input"
                value={demoSeedToday}
                onChange={(e) => setDemoSeedToday(e.target.value)}
              />
            </label>
            {visibility.showClearTestData ? (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-separator"
                  checked={demoSeedReset}
                  onChange={(e) => setDemoSeedReset(e.target.checked)}
                />
                <span className="text-label">
                  Vorher löschen (bestehende Fachdaten des Mandanten entfernen)
                </span>
              </label>
            ) : null}
          </fieldset>
          <Feedback success={demoSeedSuccess} error={demoSeedError} />
          {canWrite ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="danger"
                loading={demoSeedLoading}
                onClick={() => void onLoadDemoData()}
              >
                Demodaten laden
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
